import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";

const NAV_LINKS = [
  { href: "#how-it-works", key: "howItWorks", fallback: "How it works" },
  { href: "#compare", key: "compare", fallback: "Compare" },
  { href: "#pricing", key: "pricing", fallback: "Pricing" },
];

export const LandingHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(["common", "landing"]);
  const lp = useLocalizedPath();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/85 backdrop-blur-xl"
          : "border-b border-transparent bg-background/40 backdrop-blur"
      }`}
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <button
          onClick={() => navigate(lp("/"))}
          className="flex items-center gap-2"
          aria-label="Workloop home"
        >
          <img
            src="/workloop-logo.png"
            alt="Workloop"
            width="32"
            height="32"
            decoding="async"
            className="h-8 w-8"
          />
          <span className="font-display text-lg font-semibold tracking-tight text-foreground">
            Workloop
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {t(`landing:nav.${link.key}`, link.fallback)}
            </a>
          ))}
          <button
            onClick={() => navigate(lp("/blog"))}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("common:nav.blog")}
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Button onClick={() => navigate("/dashboard")} className="btn-gradient text-white border-0 h-9">
              {t("common:nav.dashboard")}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="text-muted-foreground hidden sm:inline-flex h-9"
                onClick={() => navigate(lp("/auth"))}
              >
                {t("common:nav.signIn")}
              </Button>
              <Button
                onClick={() => navigate(lp("/auth") + "?mode=signup")}
                className="btn-gradient text-white border-0 h-9 px-4"
              >
                {t("common:nav.getStarted")}
              </Button>
            </>
          )}

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-9 w-9"
                aria-label={t("common:nav.openMenu")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 pt-12">
              <nav className="flex flex-col gap-1 text-base">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-md px-3 py-2.5 text-foreground hover:bg-accent transition-colors"
                  >
                    {t(`landing:nav.${link.key}`, link.fallback)}
                  </a>
                ))}
                <button
                  onClick={() => { setMenuOpen(false); navigate(lp("/blog")); }}
                  className="rounded-md px-3 py-2.5 text-left text-foreground hover:bg-accent transition-colors"
                >
                  {t("common:nav.blog")}
                </button>
                {!user && (
                  <button
                    onClick={() => { setMenuOpen(false); navigate(lp("/auth")); }}
                    className="rounded-md px-3 py-2.5 text-left text-foreground hover:bg-accent transition-colors"
                  >
                    {t("common:nav.signIn")}
                  </button>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
